/**
 * CMS-wide constants. Navigation map ve bilinen route grupları burada.
 */

import type { LucideIcon } from "lucide-react";
import {
  Filter,
  Activity,
  AlertTriangle,
  BarChart3,
  Boxes,
  CreditCard,
  Database,
  DollarSign,
  FileText,
  Flag,
  Gauge,
  Heart,
  Image as ImageIcon,
  Inbox,
  Key,
  KeyRound,
  Languages,
  LayoutDashboard,
  LayoutTemplate,
  Layers,
  LifeBuoy,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquareCode,
  Palette,
  Receipt,
  ScrollText,
  Search,
  Send,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Shield,
  Sparkles,
  Stethoscope,
  Users,
  Target,
  Webhook,
  Wrench,
  Zap,
} from "lucide-react";

export const APP_NAME = "aicontent CMS";

export type NavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  badge?: "active-users" | "stuck-jobs" | "today-cost" | "anomalies" | "open-tickets";
};

export type NavSection = {
  label: string;
  items: NavItem[];
};

/**
 * Sidebar nav — 4 mantıksal grup, 20 link.
 * Group sıra: operasyonel sıcaklığa göre (sık dokunulan üstte).
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    label: "Genel",
    items: [
      { href: "/", label: "Dashboard", icon: LayoutDashboard },
      { href: "/reports", label: "Raporlar", icon: BarChart3 },
      { href: "/reports/custom", label: "Özel Rapor", icon: Database },
      { href: "/health", label: "Sistem Sağlığı", icon: Activity },
      { href: "/audit", label: "Audit", icon: ScrollText },
    ],
  },
  {
    label: "Müşteri",
    items: [
      { href: "/users", label: "Kullanıcılar", icon: Users, badge: "active-users" },
      { href: "/workspaces", label: "Workspace'ler", icon: Users },
      { href: "/subscriptions", label: "Abonelikler", icon: CreditCard },
      { href: "/plans", label: "Planlar", icon: Layers },
      { href: "/payments", label: "Ödemeler", icon: Receipt },
      { href: "/addons", label: "Backlink / Eklentiler", icon: Zap },
      { href: "/cost", label: "Maliyet", icon: DollarSign, badge: "today-cost" },
    ],
  },
  {
    label: "Sektör & İçerik",
    items: [
      { href: "/industries", label: "Sektörler", icon: Stethoscope },
      { href: "/services", label: "Alt-Hizmetler", icon: Boxes },
      { href: "/master-prompts", label: "Master Prompt Kütüphanesi", icon: MessageSquareCode },
      { href: "/content-types", label: "İçerik Tipleri", icon: FileText },
      { href: "/brand-kits", label: "Brand Kitleri", icon: Palette },
    ],
  },
  {
    label: "AI & Kalite",
    items: [
      { href: "/ai-patterns", label: "AI Pattern Kuralları", icon: ShieldCheck },
      { href: "/ai-phrases", label: "AI Phrase İmzaları", icon: Sparkles },
      { href: "/protected-terms", label: "Korunan Terimler", icon: ShieldAlert },
      { href: "/content-moderation", label: "İçerik Moderasyonu", icon: ShieldAlert },
    ],
  },
  {
    label: "İletişim",
    items: [
      { href: "/destek", label: "Destek Talepleri", icon: LifeBuoy, badge: "open-tickets" },
      { href: "/mailbox", label: "Mailbox", icon: Inbox },
      { href: "/email-templates", label: "Email Şablonları", icon: LayoutTemplate },
      { href: "/email-campaigns", label: "Email Kampanyaları", icon: Send },
      { href: "/email-automations", label: "Email Otomasyonları", icon: Zap },
    ],
  },
  {
    label: "Marketing",
    items: [
      { href: "/marketing-hub", label: "Marketing Hub", icon: Target },
      { href: "/funnel", label: "Funnel & Growth", icon: Filter },
      { href: "/marketing-texts", label: "Marketing Metinleri", icon: Languages },
      { href: "/branding", label: "Marka & Logo", icon: ImageIcon },
      { href: "/seo", label: "SEO Ayarları", icon: Search },
      { href: "/redirects", label: "Yönlendirmeler", icon: Webhook },
      { href: "/blog", label: "Blog", icon: FileText },
    ],
  },
  {
    label: "Operasyon",
    items: [
      { href: "/announcements", label: "Duyurular", icon: Megaphone },
      { href: "/feature-flags", label: "Feature Flags", icon: Flag },
      { href: "/security", label: "Güvenlik", icon: Shield },
      { href: "/webhook-events", label: "Webhook Olayları", icon: Webhook },
    ],
  },
  {
    label: "Sistem",
    items: [
      { href: "/system-settings", label: "Sistem Ayarları", icon: Settings },
      { href: "/payment-providers", label: "Ödeme Sağlayıcıları", icon: CreditCard },
      { href: "/api-pool", label: "API Havuzu & Anahtarlar", icon: KeyRound },
      { href: "/ai-models", label: "AI Modelleri", icon: Sparkles },
      { href: "/system-check", label: "Sağlık Taraması", icon: Heart },
      { href: "/jobs", label: "İşler", icon: ListChecks, badge: "stuck-jobs" },
      { href: "/recovery", label: "Recovery", icon: AlertTriangle, badge: "anomalies" },
      { href: "/analytics", label: "Analytics", icon: BarChart3 },
    ],
  },
];

/** Public paths — Supabase auth shell'i bypass eder. */
export const PUBLIC_PATHS = new Set<string>(["/login", "/forgot-password", "/reset-password"]);
